import { vec4 } from 'gl-matrix';
import { Texture } from '../texture';
import { Context } from '../context/Context';

/**
 * The full set of texture inputs and scalar parameters that drive the
 * shaders for a single render object. Mirrors the glTF material concept.
 *
 * Materials may be shared between objects (the manifest loader gives every
 * object referencing the same glTF material the same Material instance), so
 * mutating fields after assignment affects all sharers. Build the material
 * fully before assigning it to a RenderObject; assignment to
 * `RenderObject.material` is what updates render-mode/order.
 */
export class Material {
  baseColor?: Texture;
  normalMap?: Texture;
  metallicRoughnessMap?: Texture;
  /** Flat tint when no baseColor texture is bound. */
  color?: vec4;

  /**
   * Create a material with a flat color. If alpha < 1, the material is
   * translucent (rendered in the forward transparency pass via a 1×1
   * base-color texture). Opaque colors use the preset-color path instead.
   */
  static fromColor(ctx: Context, r: number, g: number, b: number, a = 1): Material {
    const mat = new Material();
    if (a < 1) {
      const gl = ctx.gl;
      const tex = new Texture();
      tex.translucent = true;
      tex.glTexture = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, tex.glTexture);
      gl.texImage2D(
        gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE,
        new Uint8Array([r * 255, g * 255, b * 255, a * 255]),
      );
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
      gl.bindTexture(gl.TEXTURE_2D, null);
      mat.baseColor = tex;
    } else {
      mat.color = vec4.fromValues(r, g, b, 1);
    }
    return mat;
  }
}
